import { Link, useParams, Navigate } from 'react-router-dom';
import { PageHero } from '../components/ui/PageHero';
import { AnimatedCard } from '../components/ui/AnimatedCard';
import { CTAButton } from '../components/ui/CTAButton';
import { RelatedLinks } from '../components/ui/RelatedLinks';
import { MEDITATION_PROGRAMS, getFeature, getMeditation } from '../constants/content';
import { featurePath, meditationPath } from '../constants/navigation';

export default function MeditationDetailPage() {
  const { id } = useParams();
  const program = getMeditation(id);

  if (!program) {
    return <Navigate to="/meditation" replace />;
  }

  const relatedFeatures = program.relatedFeatureIds
    .map((fid) => getFeature(fid))
    .filter(Boolean);

  return (
    <>
      <PageHero
        title={program.title}
        subtitle={program.desc}
        breadcrumb={[
          { label: '首页', path: '/' },
          { label: '冥想课程', path: '/meditation' },
          { label: program.title },
        ]}
      />

      <section className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
          <div className="lg:col-span-2 space-y-8">
            <AnimatedCard className="bg-white/5 rounded-2xl p-8 border border-purple-500/20">
              <div className="flex flex-wrap gap-2 mb-6">
                <span className={`text-xs ${program.badgeClass} text-light px-3 py-1 rounded-full`}>
                  {program.badge}
                </span>
                <span className="text-xs bg-white/10 text-purple-300 px-3 py-1 rounded-full">
                  {program.duration}
                </span>
                <span className="text-xs bg-secondary/20 text-light px-3 py-1 rounded-full">
                  {program.difficulty}
                </span>
              </div>
              <p className="text-purple-400 text-sm mb-6">{program.scene}</p>

              <h2 className="text-lg font-semibold text-secondary mb-4">课程结构</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {program.sections.map((sec, index) => (
                  <div
                    key={sec.name}
                    className="bg-darker/40 rounded-xl p-4 border border-purple-500/10"
                  >
                    <span className="text-purple-500 text-xs">环节 {index + 1}</span>
                    <p className="font-medium text-sm mb-1">{sec.name}</p>
                    <p className="text-purple-300 text-xs">{sec.meaning}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-lg font-semibold text-secondary mb-4">练习步骤</h2>
              <ol className="space-y-3 mb-8">
                {program.steps.map((step, i) => (
                  <li key={step} className="flex gap-3 text-purple-200 text-sm">
                    <span className="w-6 h-6 rounded-full bg-primary/30 flex items-center justify-center text-xs shrink-0">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>

              <div className="bg-primary/10 rounded-xl p-4 border border-purple-500/20">
                <p className="text-purple-200 text-sm">
                  <i className="fa fa-lightbulb-o text-secondary mr-2" />
                  {program.tips}
                </p>
              </div>
            </AnimatedCard>

            <AnimatedCard className="bg-white/5 rounded-2xl p-8 border border-purple-500/20">
              <h2 className="text-xl font-bold mb-4">适合练习目标</h2>
              <ul className="space-y-3">
                {program.exampleGoals.map((goal) => (
                  <li
                    key={goal}
                    className="flex items-start gap-3 bg-darker/30 rounded-xl p-4 border border-purple-500/10"
                  >
                    <i className="fa fa-bullseye text-purple-400 text-xs mt-1" />
                    <span className="text-purple-200 text-sm">{goal}</span>
                  </li>
                ))}
              </ul>
            </AnimatedCard>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl p-6 border border-purple-500/20 text-center">
              <h3 className="font-semibold mb-3">在 APP 中开始</h3>
              <p className="text-purple-300 text-sm mb-4">打开 Ameowzing，选择「{program.title}」</p>
              <CTAButton to="/guide" variant="primary" icon="fa-play" className="w-full">
                新手教程
              </CTAButton>
            </div>

            {relatedFeatures.length > 0 && (
              <div className="bg-white/5 rounded-2xl p-6 border border-purple-500/20">
                <h3 className="font-semibold text-secondary mb-4">相关主题</h3>
                <div className="space-y-2">
                  {relatedFeatures.map((f) => (
                    <Link
                      key={f.id}
                      to={featurePath(f.id)}
                      className="block text-purple-200 text-sm hover:text-secondary py-1"
                    >
                      {f.title} →
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <RelatedLinks
              links={[
                { to: '/meditation', label: '全部课程' },
                { to: '/features', label: '功能特色' },
                { to: '/guide', label: '使用流程' },
                ...MEDITATION_PROGRAMS.filter((m) => m.id !== id).map((m) => ({
                  to: meditationPath(m.id),
                  label: m.title,
                })),
              ]}
            />
          </div>
        </div>
      </section>
    </>
  );
}
